import type { PortalCatalogItem } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api, newIdempotencyKey } from '../api';
import { useAuth } from '../auth';
import { TopBar } from '../components/Layout';
import { Diamond } from '../components/brand';
import { Empty, ErrorBox, OfflineBanner, PageHead, QueryView, SearchBox, StatusBadge } from '../components/ui';
import { formatDate, formatMoney, formatQty, weekdayName } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';
import { InvoiceSheet } from './PublicDocumentPage';

/**
 * The customer portal (docs/VISION.md row 8b): what a customer sees of their own business with us. Same
 * phone sign-in, their own prices, their orders, their account. Nothing of anyone else's.
 */
export function PortalLayout() {
  const { t } = useLocale();
  const { me, signOut } = useAuth();
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  const tabs = [
    { to: '/portal', label: t('home'), end: true },
    { to: '/portal/order', label: t('portalNewOrder'), end: false },
    { to: '/portal/orders', label: t('portalOrders'), end: false },
    { to: '/portal/account', label: t('portalStatement'), end: false },
  ];
  return (
    <div className="app portal">
      <div className="frame">
        <div className="top">
          <TopBar />
          <OfflineBanner />
        </div>
        <main className="content">
          <Outlet />
          <p className="muted portal-foot">
            {t('signedInAs')} {me?.name}.{' '}
            <button type="button" className="btn btn-ghost" onClick={() => void signOut()}>
              {t('signOut')}
            </button>
          </p>
        </main>
        <nav className="tabbar">
          {tabs.map((s) => (
            <NavLink key={s.to} to={s.to} end={s.end} className="tab">
              {({ isActive }) => (
                <>
                  <Diamond filled={isActive} />
                  <span>{s.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}

export function PortalHomePage() {
  const { t, intlLocale } = useLocale();
  const home = useQuery({ queryKey: qk.portalHome, queryFn: ({ signal }) => api.portalHome(signal) });
  return (
    <div className="stack">
      <QueryView query={home}>
        {(h) => (
          <>
            <section>
              <h1 className="greeting">
                {t('portalGreeting')}, {h.customer.name}
              </h1>
              <p className="muted date-line">
                {t('portalNextDelivery')}: {formatDate(h.nextDeliveryDate, intlLocale, 'long')}
                {h.customer.deliveryDays.length ? ` · ${h.customer.deliveryDays.map((d) => weekdayName(d, intlLocale)).join(', ')}` : ''}
              </p>
            </section>
            <Link to="/portal/order" className="btn btn-primary btn-lg btn-block">
              {t('portalNewOrder')}
            </Link>
            {h.lastOrder ? (
              <Link to="/portal/order" state={{ sameAgain: h.lastOrder.lines }} className="btn btn-secondary btn-lg btn-block">
                {t('portalSameAgain')}
              </Link>
            ) : null}
            {h.lastOrder ? (
              <Link to={`/portal/orders/${h.lastOrder.id}`} className="card stack-sm">
                <span className="muted">
                  {t('portalLastOrder')} · {formatDate(h.lastOrder.requestedDate, intlLocale, 'day')}
                </span>
                <span>
                  {h.lastOrder.lines.map((l) => `${formatQty(l.qty)} ${l.itemName}`).join(', ')}
                </span>
                <strong>{formatMoney(h.lastOrder.total, intlLocale)}</strong>
              </Link>
            ) : null}
            <Link to="/portal/account" className="card balance-card">
              <span className="muted">{t('balance')}</span>
              <strong className={h.balance !== null && h.balance > 0 ? 'owed' : undefined}>{h.balance === null ? '—' : formatMoney(h.balance, intlLocale)}</strong>
              {h.openInvoices ? (
                <span className="muted">
                  {h.openInvoices} {t('portalOpenInvoices').toLowerCase()}
                </span>
              ) : null}
            </Link>
          </>
        )}
      </QueryView>
    </div>
  );
}

type Preset = { itemId: string; qty: number }[];

export function PortalOrderPage() {
  const { t, intlLocale, locale } = useLocale();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const location = useLocation();
  const preset = (location.state as { sameAgain?: Preset } | null)?.sameAgain;
  const catalog = useQuery({ queryKey: qk.portalCatalog, queryFn: ({ signal }) => api.portalCatalog(signal) });
  const dates = useQuery({ queryKey: qk.portalDates, queryFn: ({ signal }) => api.portalDates(signal) });
  const home = useQuery({ queryKey: qk.portalHome, queryFn: ({ signal }) => api.portalHome(signal) });
  const [q, setQ] = useState('');
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');
  const [qty, setQty] = useState<Record<string, string>>({});
  const [key] = useState(() => newIdempotencyKey());
  useEffect(() => {
    if (dates.data?.[0] && (!date || !dates.data.includes(date))) setDate(dates.data[0]);
  }, [dates.data, date]);
  useEffect(() => {
    if (preset) setQty(Object.fromEntries(preset.map((l) => [l.itemId, String(l.qty)])));
  }, [preset]);
  const place = useMutation({
    mutationFn: () =>
      api.placePortalOrder(
        {
          requestedDate: date,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
          lines: Object.entries(qty)
            .map(([itemId, v]) => ({ itemId, qty: Number(v) }))
            .filter((l) => Number.isFinite(l.qty) && l.qty > 0),
        },
        key,
      ),
    onSuccess: (o) => {
      void qc.invalidateQueries({ queryKey: ['portal'] });
      navigate(`/portal/orders/${o.id}`, { state: { placed: true } });
    },
    // past the cut-off while the screen was open: the days offered move on
    onError: () => {
      void qc.invalidateQueries({ queryKey: qk.portalDates });
      void qc.invalidateQueries({ queryKey: qk.portalHome });
    },
  });
  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    const all = catalog.data ?? [];
    const shown = term ? all.filter((i) => i.name.toLowerCase().includes(term) || (i.nameEs ?? '').toLowerCase().includes(term)) : all;
    // what they usually take first, then the rest by name
    return [...shown].sort((a, b) => Number(b.lastQty !== null) - Number(a.lastQty !== null) || a.name.localeCompare(b.name));
  }, [catalog.data, q]);
  const picked = Object.entries(qty).filter(([, v]) => Number(v) > 0);
  const total = picked.reduce((s, [id, v]) => {
    const it = catalog.data?.find((i) => i.itemId === id);
    return it?.price != null ? s + it.price * Number(v) : s;
  }, 0);
  const label = (i: PortalCatalogItem) => (locale === 'es' && i.nameEs ? i.nameEs : i.name);

  return (
    <div className="stack">
      <PageHead title={t('portalNewOrder')} back="/portal" />
      <label className="field">
        <span>{t('portalDeliverOn')}</span>
        <select value={date} onChange={(e) => setDate(e.target.value)} aria-label={t('portalDeliverOn')}>
          {(dates.data ?? []).map((d) => (
            <option key={d} value={d}>
              {formatDate(d, intlLocale, 'long')}
            </option>
          ))}
        </select>
      </label>
      {home.data ? (
        <p className="muted" data-testid="cutoff">
          {t('portalCutoffHint').replace('{time}', clockLabel(home.data.cutoff, intlLocale))}
        </p>
      ) : null}
      <label className="field">
        <span>{t('portalNotes')}</span>
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>
      <SearchBox value={q} onChange={setQ} />
      <QueryView query={catalog}>
        {() =>
          rows.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <ul className="list" data-testid="catalog">
              {rows.map((i) => (
                <li key={i.itemId} className="row">
                  <span className="row-main">
                    <span className="row-title">{label(i)}</span>
                    <span className="row-sub">
                      {i.packDesc ?? i.packUnit} · {i.price === null ? t('portalAsk') : formatMoney(i.price, intlLocale)}
                      {i.lastQty !== null ? ` · ${t('portalLastTime')} ${formatQty(i.lastQty)}` : ''}
                    </span>
                  </span>
                  <input
                    className="count-input"
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    aria-label={`${t('qty')} ${i.name}`}
                    placeholder="0"
                    value={qty[i.itemId] ?? ''}
                    onChange={(e) => setQty((cur) => ({ ...cur, [i.itemId]: e.target.value }))}
                  />
                </li>
              ))}
            </ul>
          )
        }
      </QueryView>
      {place.error ? <ErrorBox error={place.error} /> : null}
      <div className="actionbar">
        <div className="total">
          <span>{picked.length ? `${picked.length} · ${formatMoney(total, intlLocale)}` : t('portalEmpty')}</span>
        </div>
        <button type="button" className="btn btn-primary btn-lg" onClick={() => place.mutate()} disabled={place.isPending || picked.length === 0 || !date}>
          {place.isPending ? t('sending') : t('portalPlace')}
        </button>
      </div>
    </div>
  );
}

export function PortalOrdersPage() {
  const { t, intlLocale } = useLocale();
  const orders = useQuery({ queryKey: qk.portalOrders, queryFn: ({ signal }) => api.portalOrders(signal) });
  return (
    <div className="stack">
      <PageHead title={t('portalMyOrders')} back="/portal" />
      <QueryView query={orders}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <ul className="list">
              {rows.map((o) => (
                <li key={o.id}>
                  <Link to={`/portal/orders/${o.id}`} className="row">
                    <span className="row-main">
                      <span className="row-title">
                        {t('portalOrderNo')} {o.orderNo} · {formatDate(o.requestedDate, intlLocale, 'day')}
                      </span>
                      <span className="row-sub">
                        <StatusBadge status={o.status} /> {o.lines.length} {t('lines').toLowerCase()}
                      </span>
                    </span>
                    <span className="row-end">
                      <span className="row-end-value">{formatMoney(o.total, intlLocale)}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )
        }
      </QueryView>
    </div>
  );
}

export function PortalOrderDetailPage() {
  const { t, intlLocale } = useLocale();
  const { id = '' } = useParams();
  const location = useLocation();
  const qc = useQueryClient();
  const placed = (location.state as { placed?: boolean } | null)?.placed === true;
  const order = useQuery({ queryKey: qk.portalOrder(id), queryFn: ({ signal }) => api.portalOrder(id, signal) });
  const home = useQuery({ queryKey: qk.portalHome, queryFn: ({ signal }) => api.portalHome(signal) });
  const cancel = useMutation({
    mutationFn: () => api.cancelPortalOrder(id),
    onSuccess: (o) => {
      qc.setQueryData(qk.portalOrder(id), o);
      void qc.invalidateQueries({ queryKey: ['portal'] });
    },
  });
  const first = home.data?.firstOpenDay;
  return (
    <div className="stack">
      <QueryView query={order}>
        {(o) => (
          <>
            <PageHead title={`${t('portalOrderNo')} ${o.orderNo}`} back="/portal/orders" />
            {placed ? (
              <p className="note" role="status">
                {t('portalPlaced')}
              </p>
            ) : null}
            <div className="badges">
              <StatusBadge status={o.status} />
              <span className="muted">{formatDate(o.requestedDate, intlLocale, 'long')}</span>
            </div>
            <ul className="list">
              {o.lines.map((l) => (
                <li key={l.id} className="row">
                  <span className="row-main">
                    <span className="row-title">{l.itemName}</span>
                    <span className="row-sub">
                      {formatQty(l.qty)} {l.unit}
                      {l.note ? ` · ${l.note}` : ''}
                    </span>
                  </span>
                  <span className="row-end">
                    <span className="row-end-value">{l.unitPrice === null ? t('portalAsk') : formatMoney(l.unitPrice * l.qty, intlLocale)}</span>
                  </span>
                </li>
              ))}
            </ul>
            <dl className="facts totals">
              <div className="fact">
                <dt>{t('total')}</dt>
                <dd className="money">{formatMoney(o.total, intlLocale)}</dd>
              </div>
            </dl>
            {o.notes ? <p className="muted">{o.notes}</p> : null}
            {o.status === 'draft' && o.channel === 'web' && first && o.requestedDate >= first ? (
              <button type="button" className="btn btn-ghost btn-block" disabled={cancel.isPending} onClick={() => window.confirm(t('portalCancelAsk')) && cancel.mutate()}>
                {t('portalCancel')}
              </button>
            ) : o.status === 'draft' || o.status === 'confirmed' ? (
              <p className="muted">{t('portalCallToChange')}</p>
            ) : null}
            {cancel.error ? <ErrorBox error={cancel.error} /> : null}
          </>
        )}
      </QueryView>
    </div>
  );
}

export function PortalStatementPage() {
  const { t, intlLocale } = useLocale();
  const st = useQuery({ queryKey: qk.portalStatement, queryFn: ({ signal }) => api.portalStatement(signal) });
  const invoices = useQuery({ queryKey: qk.portalInvoices, queryFn: ({ signal }) => api.portalInvoices(signal) });
  return (
    <div className="stack">
      <PageHead title={t('portalStatement')} back="/portal" />
      <QueryView query={st}>
        {(s) => (
          <>
            <div className="card balance-card">
              <span className="muted">{t('balance')}</span>
              <strong className={s.balance !== null && s.balance > 0 ? 'owed' : undefined}>{s.balance === null ? '—' : formatMoney(s.balance, intlLocale)}</strong>
              <span className="muted">
                {t('terms')}: {s.termsDays} {t('days')}
              </span>
            </div>
            <h2>{t('portalOpenInvoices')}</h2>
            {s.openInvoices.length === 0 ? (
              <Empty>{t('noResults')}</Empty>
            ) : (
              <ul className="list">
                {s.openInvoices.map((inv) => (
                  <li key={inv.id}>
                    <Link to={`/portal/invoices/${inv.id}`} className="row">
                      <span className="row-main">
                        <span className="row-title">
                          {t('invoice')} {inv.invoiceNo}
                        </span>
                        <span className="row-sub">
                          {formatDate(inv.date, intlLocale, 'date')}
                          {inv.daysLate > 0 ? <span className="badge badge-danger">{inv.daysLate} {t('days')}</span> : null}
                        </span>
                      </span>
                      <span className="row-end">
                        <span className="row-end-value owed">{formatMoney(inv.balance, intlLocale)}</span>
                        <span className="row-end-label">{formatMoney(inv.total, intlLocale)}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </QueryView>
      <h2>{t('portalInvoices')}</h2>
      <QueryView query={invoices}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <ul className="list" data-testid="portal-invoices">
              {rows.map((inv) => (
                <li key={inv.id}>
                  <Link to={`/portal/invoices/${inv.id}`} className="row">
                    <span className="row-main">
                      <span className="row-title">
                        {t('invoice')} {inv.invoiceNo}
                      </span>
                      <span className="row-sub">{formatDate(inv.invoiceDate, intlLocale, 'date')}</span>
                    </span>
                    <span className="row-end">
                      <span className="row-end-value">{formatMoney(inv.total, intlLocale)}</span>
                      {inv.balance !== null && inv.balance > 0 ? <span className="row-end-label owed">{formatMoney(inv.balance, intlLocale)}</span> : null}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )
        }
      </QueryView>
    </div>
  );
}

/** One of their invoices as printed (decision 110), with Print. */
export function PortalInvoicePage() {
  const { t } = useLocale();
  const { id = '' } = useParams();
  const inv = useQuery({ queryKey: qk.portalInvoice(id), queryFn: ({ signal }) => api.portalInvoice(id, signal) });
  return (
    <div className="stack public-doc">
      <div className="no-print">
        <PageHead title={t('invoice')} back="/portal/account" />
      </div>
      <QueryView query={inv}>
        {(d) => (
          <>
            <button type="button" className="btn btn-secondary no-print" onClick={() => window.print()}>
              {t('print')}
            </button>
            <InvoiceSheet d={d} />
          </>
        )}
      </QueryView>
    </div>
  );
}

/** 14:00 as the locale writes a time of day. */
function clockLabel(hhmm: string, intlLocale: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  return new Intl.DateTimeFormat(intlLocale, { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 0, 1, h ?? 0, m ?? 0)));
}
